import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Users, Building2, CreditCard, Calendar, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAuthToken } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DealerSignupsTab = ({ isDark }) => {
  const [dealers, setDealers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');

  useEffect(() => {
    fetchDealers();
  }, [statusFilter, planFilter]);

  const fetchDealers = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken();
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (planFilter !== 'all') params.plan = planFilter;

      const res = await axios.get(`${API}/admin/dealer-signups`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDealers(res.data);
    } catch (err) {
      console.error('Error fetching dealer signups:', err);
      toast.error('Failed to load dealer signups');
    } finally {
      setIsLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: 'bg-green-100 text-green-800',
      trial: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-600',
      cancelled: 'bg-red-100 text-red-800',
      expired: 'bg-orange-100 text-orange-800',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="dealer-signups-loading">
        <Loader2 className="animate-spin text-[#004E86]" size={36} />
      </div>
    );
  }

  return (
    <div data-testid="dealer-signups-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center gap-2 mb-1">
            <Users size={16} className="text-[#004E86]" />
            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Advisors</span>
          </div>
          <p className="text-2xl font-bold text-[#004E86]" data-testid="total-dealers">{dealers.length}</p>
        </div>
        <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={16} className="text-green-600" />
            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Active</span>
          </div>
          <p className="text-2xl font-bold text-green-600" data-testid="active-dealers">
            {dealers.filter(d => d.subscription_status === 'active').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center gap-2 mb-1">
            <CreditCard size={16} className="text-purple-600" />
            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Trial</span>
          </div>
          <p className="text-2xl font-bold text-purple-600" data-testid="trial-dealers">
            {dealers.filter(d => d.subscription_status === 'trial').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={16} className="text-orange-600" />
            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Inactive</span>
          </div>
          <p className="text-2xl font-bold text-orange-600" data-testid="inactive-dealers">
            {dealers.filter(d => d.subscription_status === 'inactive' || d.subscription_status === 'cancelled').length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="plan-filter">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="yearly">Yearly</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className={`rounded-lg shadow-sm overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        {dealers.length === 0 ? (
          <div className="text-center py-12" data-testid="no-dealers">
            <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No advisors found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`} data-testid="dealer-signups-table">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Advisor</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Plan</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Last Payment</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Signup Date</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {dealers.map((d, i) => (
                  <tr key={d.id} data-testid={`dealer-row-${i}`} className={isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.business_name}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{d.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-sm font-medium capitalize ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {d.subscription_plan || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusBadge(d.subscription_status)}`}>
                        {d.subscription_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {d.latest_payment ? (
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            ${d.latest_payment.amount?.toFixed(2) || '0.00'}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {d.latest_payment.status} - {formatDate(d.latest_payment.created_at)}
                          </p>
                        </div>
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No payments</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formatDate(d.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerSignupsTab;
